// C-14 "PF internal message notification" (task 24, D-81): the template fields, the preview renderer and the
// tab's problems and warning. Mirrors the API's `lib/pf-message-template.ts` (the server checks the same rules).
import type { Settings } from './types'

/** Every placeholder a template may use, in the Insert field menu's order. */
export const PF_MESSAGE_FIELDS = [
  'Visit type',
  'Provider',
  'Location',
  'Source',
  'Date',
  'Time',
  'Patient name',
  'Patient PRN',
  'Patient DOB',
] as const
export type PfMessageField = (typeof PF_MESSAGE_FIELDS)[number]

/** Task 26 (D-83): the only placeholders the conflict line may use (and the subject and message may not). */
export const PF_MESSAGE_CONFLICT_FIELDS = ['Conflict reason', 'Conflict location', 'Conflict time'] as const
export type PfMessageConflictField = (typeof PF_MESSAGE_CONFLICT_FIELDS)[number]

/** The preview's sample conflict (invented). */
export const SAMPLE_CONFLICT_VALUES: Record<PfMessageConflictField, string> = {
  'Conflict reason': 'Out of office',
  'Conflict location': 'Pasadena Clinic',
  'Conflict time': '8:00 AM – 12:00 PM',
}

export const PF_MESSAGE_TEXT_MAX = 500

/** The preview's sample values (invented). */
export const SAMPLE_VALUES: Record<PfMessageField, string> = {
  'Visit type': 'Follow-Up Visit',
  Provider: 'Dr. Sarah Mitchell',
  Location: 'Primary Care Center – Downtown',
  Source: 'website',
  Date: 'Mon, Nov 3, 2026',
  Time: '9:00 AM',
  'Patient name': 'Jane Sample',
  'Patient PRN': 'PCC-000123',
  'Patient DOB': '04/12/1986',
}

export const PF_MESSAGE_COPY = {
  tab: 'PF internal message notification',
  purpose: 'When a patient books online, Practice Fusion sends this message to the people you choose.',
  to: 'To',
  inPractice: 'In practice',
  search: 'Search people',
  people: 'Matching people',
  showPeople: 'Show people',
  hidePeople: 'Hide people',
  noMatches: 'No one matches',
  everyonePicked: 'Everyone is already a recipient',
  empty: 'No people from Practice Fusion yet. Use Refresh from PF on Appointment settings.',
  regarding: 'Regarding patient',
  regardingValue: 'The patient who booked (automatic)',
  subject: 'Subject',
  message: 'Message',
  insertField: 'Insert field',
  reset: 'Reset to default',
  addToChart: 'Add to chart',
  urgent: 'Urgent',
  alwaysOff: 'Always off',
  preview: 'Preview',
  conflictLine: 'Conflict line',
  conflictHelp: 'Added to the message, once per conflict, when the patient booked over something at another location',
  removedInPf: 'Removed in PF',
  noRecipients: "No recipients selected. Booking messages won't be sent until you choose at least one person.",
  unknownField: (name: string) => `Unknown field {${name}}. Use Insert field to add one.`,
  tooLong: 'Keep this under 500 characters.',
}

const TOKEN = /\{([^{}\r\n]{1,40})\}/g
const KNOWN: ReadonlySet<string> = new Set<string>(PF_MESSAGE_FIELDS)
const KNOWN_CONFLICT: ReadonlySet<string> = new Set<string>(PF_MESSAGE_CONFLICT_FIELDS)

/** Which fields a template may use: the subject's and message's, or the conflict line's (task 26). */
export type TemplateKind = 'message' | 'conflict'
const knownOf = (kind: TemplateKind) => (kind === 'conflict' ? KNOWN_CONFLICT : KNOWN)

/** Code points (an emoji counts once), as the server counts. */
export const textLength = (s: string) => Array.from(s).length

/** The unknown `{…}` names in a template (`conflict`: anything but the three conflict fields). */
export function unknownFields(template: string, kind: TemplateKind = 'message'): string[] {
  const known = knownOf(kind)
  const out = new Set<string>()
  for (const m of template.matchAll(TOKEN)) {
    const name = m[1] ?? ''
    if (!known.has(name)) out.add(name)
  }
  return [...out]
}

const cut = (s: string) =>
  textLength(s) <= PF_MESSAGE_TEXT_MAX ? s : Array.from(s).slice(0, PF_MESSAGE_TEXT_MAX).join('')

/** Fills the known fields; unknown `{…}` stay as written; cut to 500 characters like the server. */
export function renderTemplate(template: string, values: Record<PfMessageField, string> = SAMPLE_VALUES): string {
  const out = template.replace(TOKEN, (whole, name: string) =>
    KNOWN.has(name) ? values[name as PfMessageField] : whole,
  )
  return cut(out)
}

/** The conflict line with the sample conflict (task 26). */
export function renderConflictLine(template: string): string {
  const out = template.replace(TOKEN, (whole, name: string) =>
    KNOWN_CONFLICT.has(name) ? SAMPLE_CONFLICT_VALUES[name as PfMessageConflictField] : whole,
  )
  return cut(out)
}

/** The preview's message: the rendered message, then one sample conflict line on a new line, cut to 500 (task 26). */
export const previewBody = (pf: PfMessages): string =>
  cut(`${renderTemplate(bodyOf(pf))}\n${renderConflictLine(conflictLineOf(pf))}`)

export type PfMessages = Settings['pfMessages']
export type PfContact = PfMessages['contacts'][number]

/** The text a template field shows: the saved text, or the default while it's `null`. */
export const subjectOf = (pf: PfMessages) => pf.subject ?? pf.defaults.subject
export const bodyOf = (pf: PfMessages) => pf.body ?? pf.defaults.body
export const conflictLineOf = (pf: PfMessages) => pf.conflictLine ?? pf.defaults.conflictLine

/**
 * The people the To picker lists: not picked yet (the picked ones are the chips above it), not removed in PF, and
 * containing `query` (any case); A–Z by name.
 */
export function selectablePeople(contacts: readonly PfContact[], query: string): PfContact[] {
  const needle = query.trim().toLowerCase()
  return contacts
    .filter((c) => !c.isRecipient && !c.removedInPf && (needle === '' || c.name.toLowerCase().includes(needle)))
    .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }))
}

/** Picked and not removed in PF: the people who get the message. */
export const activeRecipients = (pf: PfMessages) => pf.contacts.filter((c) => c.isRecipient && !c.removedInPf)

/** One template's problem (the first), or null. */
export function templateProblem(text: string, kind: TemplateKind = 'message'): string | null {
  const unknown = unknownFields(text, kind)[0]
  if (unknown !== undefined) return PF_MESSAGE_COPY.unknownField(unknown)
  if (textLength(text) > PF_MESSAGE_TEXT_MAX) return PF_MESSAGE_COPY.tooLong
  return null
}

/** Save-blocking problems (only while the feature is on: the tab is hidden otherwise). */
export function pfMessageProblems(s: Settings): string[] {
  const pf = s.pfMessages
  if (!pf.enabled) return []
  const out: string[] = []
  const subject = templateProblem(subjectOf(pf))
  if (subject) out.push(`Subject: ${subject}`)
  const body = templateProblem(bodyOf(pf))
  if (body) out.push(`Message: ${body}`)
  const conflict = templateProblem(conflictLineOf(pf), 'conflict')
  if (conflict) out.push(`${PF_MESSAGE_COPY.conflictLine}: ${conflict}`)
  return out
}

/** The red warning: the feature is on and nobody active is picked. */
export const needsRecipients = (s: Settings) => s.pfMessages.enabled && activeRecipients(s.pfMessages).length === 0
